import { StringTransformer } from "../utils/StringTransformer"
import { Formatter } from "./formatter"

export interface StringFormatterOptions<TInput> {
  /**
   *  The fallback value to use if the input is falsy.
   *
   *  @default: ""
   */
  fallback?: string | ((input: TInput) => string)

  /**
   *  A function to determine if the input should be formatted. If the function
   *  returns false, the formatter wll use the fallback. Otherwise, it will
   *  proceed with formatting.
   *
   *  @default: (input: TInput) => !!input
   */
  shouldFormat?: (input: TInput) => boolean

  /**
   *  An optional transformer to apply to the input after transforming
   *  the value into a string.
   *
   *  @default: no/empty transformer is applied
   */
  transformer?: StringTransformer
}

// an internal format to ensure that all options are set in the constructor
type StringFormatterSettings<TInput> = Required<StringFormatterOptions<TInput>>

/**
 *  This is a class that formats an input into a string. We use subclasses
 *  of it to cast our specific types into a string with a specific format.
 *  For example, a phone number has a number of formats that we would like
 *  to display in the frontend.
 */
export abstract class StringFormatter<TInput> implements Formatter<TInput> {
  private options: StringFormatterSettings<TInput>

  constructor(initialOptions: StringFormatterOptions<TInput> = {}) {
    this.options = {
      fallback: initialOptions.fallback ?? "",
      shouldFormat: initialOptions.shouldFormat ?? ((input: TInput) => !!input),
      transformer: initialOptions.transformer ?? new StringTransformer(),
    }
  }

  /**
   *  Convert the input into a string.
   */
  protected abstract convert(input: TInput): string

  /**
   *  Format the input into a string.
   */
  format(input: TInput): string {
    // if the input is falsy
    if (!this.options.shouldFormat(input)) {
      return this.processFallback(input)
    }

    // convert the input into a string
    let result = this.convert(input)

    // apply the transformer if we have one set
    if (this.options.transformer) {
      result = this.options.transformer.transform(result)
    }

    // return the result of formatting
    return result
  }

  private processFallback(input: TInput): string {
    if (typeof this.options.fallback === "function") {
      return this.options.fallback(input)
    }

    return this.options.fallback ?? ""
  }
}
